'use strict';
import isUndefined from 'lodash/isUndefined';
import {getDomainLink} from '../../core-utilities/domain/ReduxUtils';
import {getAssetProject, getProject} from '../../app/model/reducers/state';
import {TaskType} from '../../tasks/enums/TaskType';


export class ParentStatusService {
  static SERVICE_NAME = 'parentStatusService';

  static $inject = [
    '$ngRedux',
  ];

  constructor(
    $ngRedux,
  ) {
    this.$ngRedux = $ngRedux;
  }

  /**
   * @param {Object} [project]
   * @return {Boolean}
   */
  isProjectLocked(project) {
    if (isUndefined(project))
      project = getProject(this.$ngRedux.getState());

    return isCompletableLocked(project);
  }

  /**
   * @param {Object} [assetProject]
   * @return {Boolean}
   */
  isAssetProjectLocked(assetProject) {
    if (this.isProjectLocked())
      return true;

    if (isUndefined(assetProject))
      assetProject = getAssetProject(this.$ngRedux.getState());

    return isCompletableLocked(assetProject);
  }

  /**
   * @param {Object} task
   * @return {Boolean}
   */
  isTaskLocked(task) {
    if (this.isProjectLocked())
      return true;

    return isApprovableLocked(task);
  }

  /**
   * @param {Object} assetTask
   * @return {Boolean}
   */
  isAssetTaskLocked(assetTask) {
    if (this.isAssetProjectLocked())
      return true;

    return isApprovableLocked(assetTask);
  }

  /**
   * @param {Object} task
   * @param {Object} req
   * @return {Boolean}
   */
  isReqLocked(task, req) {
    return this.isProjectLocked() || isApprovableLocked(task) || isApprovableLocked(req);
  }

  /**
   * @param {Object} task
   * @param {Object} req
   * @return {Boolean}
   */
  isAssetReqLocked(task, req) {
    return this.isAssetProjectLocked() || isApprovableLocked(task) || isApprovableLocked(req);
  }

  /**
   * @param {Object} req
   * @return {Promise<Boolean>}
   */
  isReqLockedAsync(req) {
    if (this.isProjectLocked())
      return Promise.resolve(true);

    return getDomainLink(req, 'task').then(task => isApprovableLocked(task) || isApprovableLocked(req));
  }

  /**
   * @param {Object} req
   * @return {Promise<Boolean>}
   */
  isAssetReqLockedAsync(req) {
    if (this.isAssetProjectLocked())
      return Promise.resolve(true);

    return getDomainLink(req, 'assetTask').then(task => isApprovableLocked(task) || isApprovableLocked(req));
  }

  // Convenience methods

  /**
   * @param {TaskType} taskType
   * @return {Boolean}
   */
  isParentProjectLocked(taskType) {
    if (taskType === TaskType.ASSET) {
      return this.isAssetProjectLocked();
    } else {
      return this.isProjectLocked();
    }
  }

  /**
   * @param {TaskType} taskType
   * @param {Object} task
   * @return {Boolean}
   */
  isParentTaskLocked(taskType, task) {
    if (taskType === TaskType.ASSET) {
      return this.isAssetTaskLocked(task);
    } else {
      return this.isTaskLocked(task);
    }
  }

  /**
   * @param {TaskType} taskType
   * @param {Object} task
   * @param {Object} req
   * @return {Boolean}
   */
  isParentReqLocked(taskType, task, req) {
    if (taskType === TaskType.ASSET) {
      return this.isAssetReqLocked(task, req);
    } else {
      return this.isReqLocked(task, req);
    }
  }

  /**
   * @param {TaskType} taskType
   * @param {Object} req
   * @return {Promise<Boolean>}
   */
  isParentReqLockedAsync(taskType, req) {
    if (taskType === TaskType.ASSET) {
      return this.isAssetReqLockedAsync(req);
    } else {
      return this.isReqLockedAsync(req);
    }
  }
}

/**
 * @param {Object} project A Project or AssetProject.
 * @return {Boolean}
 */
export function isCompletableLocked(project) {
  return project && (project.status === 'complete' || project.status === 'cancelled');
}

/**
 * @param {Object} task A Project or AssetProject.
 * @return {Boolean}
 */
export function isApprovableLocked(task) {
  return task && (task.status === 'approved' || task.status === 'cancelled');
}

/**
 * @param {String} status A Project or AssetProject.
 * @return {Boolean}
 */
export function isLockedStatus(status) {
  return status === 'complete' || status === 'approved' || status === 'cancelled';
}



// WEBPACK FOOTER //
// ./src/js/projects/services/ParentStatusService.js